import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from '@/components/ui/dialog';
import { Plus, Trash2, RefreshCw, Search, Loader2, RotateCcw, Calculator } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const authHeaders = () => ({
  Authorization: 'Bearer ' + (localStorage.getItem('token') || ''),
});

/**
 * Admin screen — Margin Master CRUD + reseed.
 * The "Margin Master" lookup table powers the auto-fill of Outward Selling
 * Price on new product creation + new invoice lines (when Unit=PCS).
 *
 * Power buttons:
 *   - Reseed from CSV: wipes the table and reloads the canonical 102 rows
 *   - Add Row: opens a modal to insert/upsert a (inward%, our%) pair
 *   - Inline edit: double-click "Our Margin" cell to edit, Enter to save
 *   - Delete: removes a row (with confirm)
 */
export default function MarginMasterAdmin() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [savingKey, setSavingKey] = useState(null); // inward_margin_pct currently being edited
  const [editingValue, setEditingValue] = useState('');
  const [editingKey, setEditingKey] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [newInward, setNewInward] = useState('');
  const [newOur, setNewOur] = useState('');
  const [reseedOpen, setReseedOpen] = useState(false);

  useEffect(() => { loadRows(); }, []);

  async function loadRows() {
    setLoading(true);
    try {
      const res = await axios.get(API + '/margin-master', { headers: authHeaders() });
      setRows(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      toast.error('Failed to load Margin Master: ' + (err.response?.data?.detail || err.message));
    } finally {
      setLoading(false);
    }
  }

  async function handleSaveRow(inward, our) {
    setSavingKey(inward);
    try {
      const body = { inward_margin_pct: Number(inward), our_margin_pct: Number(our) };
      await axios.post(API + '/margin-master/upsert', body, { headers: authHeaders() });
      toast.success('Row saved · ' + Number(inward).toFixed(2) + '% → ' + Number(our).toFixed(2) + '%');
      setEditingKey(null);
      await loadRows();
    } catch (err) {
      toast.error('Save failed: ' + (err.response?.data?.detail || err.message));
    } finally {
      setSavingKey(null);
    }
  }

  async function handleDelete(inward) {
    if (!window.confirm('Delete row at Inward Margin = ' + Number(inward).toFixed(2) + '%?')) return;
    try {
      await axios.delete(API + '/margin-master/row', {
        headers: authHeaders(), params: { inward_margin_pct: inward },
      });
      toast.success('Row deleted');
      await loadRows();
    } catch (err) {
      toast.error('Delete failed: ' + (err.response?.data?.detail || err.message));
    }
  }

  async function handleReseed() {
    try {
      const res = await axios.post(API + '/margin-master/seed', null, {
        headers: authHeaders(), params: { force: true },
      });
      toast.success('Reseeded · ' + (res.data?.count || 0) + ' rows');
      setReseedOpen(false);
      await loadRows();
    } catch (err) {
      toast.error('Reseed failed: ' + (err.response?.data?.detail || err.message));
    }
  }

  async function handleAddRow() {
    const inward = parseFloat(newInward);
    const our = parseFloat(newOur);
    if (Number.isNaN(inward) || Number.isNaN(our)) {
      toast.error('Both fields must be valid numbers');
      return;
    }
    await handleSaveRow(inward, our);
    setAddOpen(false);
    setNewInward('');
    setNewOur('');
  }

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) => {
      const i = Number(r.inward_margin_pct).toFixed(2);
      const o = Number(r.our_margin_pct).toFixed(2);
      return i.includes(q) || o.includes(q);
    });
  }, [rows, search]);

  return (
    <div className="space-y-4" data-testid="margin-master-admin">
      <Card>
        <CardHeader className="flex flex-row items-start justify-between gap-4">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Calculator className="w-5 h-5 text-blue-600" />
              Margin Master
            </CardTitle>
            <p className="text-xs text-gray-500 mt-1 max-w-2xl">
              Lookup table that drives the auto-fill of <strong>Outward Selling Price</strong> on
              new products and new invoice lines (when Unit = PCS). The raw inward margin is
              normalised to <code className="text-[11px] bg-gray-100 px-1 rounded">round(x) + 0.01</code> and
              matched against this table to fetch the company's target margin %.
            </p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <Dialog open={reseedOpen} onOpenChange={setReseedOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" size="sm" data-testid="margin-master-reseed-btn">
                  <RotateCcw className="w-4 h-4 mr-1.5" /> Reseed from CSV
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle>Reseed Margin Master?</DialogTitle></DialogHeader>
                <p className="text-sm text-gray-600">
                  This will <strong>wipe</strong> all current rows and reload the canonical 102-row
                  CSV seed. Any custom rows or edits you've made will be lost. Continue?
                </p>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setReseedOpen(false)}>Cancel</Button>
                  <Button onClick={handleReseed} className="bg-red-600 hover:bg-red-700" data-testid="margin-master-reseed-confirm-btn">
                    Yes, reseed
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
            <Dialog open={addOpen} onOpenChange={setAddOpen}>
              <DialogTrigger asChild>
                <Button size="sm" className="bg-blue-600 hover:bg-blue-700" data-testid="margin-master-add-btn">
                  <Plus className="w-4 h-4 mr-1.5" /> Add Row
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle>Add / update margin row</DialogTitle></DialogHeader>
                <div className="space-y-3">
                  <div className="space-y-1">
                    <label className="text-xs font-medium text-gray-700">Inward Margin %</label>
                    <Input
                      type="number" step="0.01" placeholder="e.g. 15.01"
                      value={newInward} onChange={(e) => setNewInward(e.target.value)}
                      data-testid="margin-master-new-inward-input"
                    />
                    <p className="text-[11px] text-gray-500">
                      Use the <strong>X.01</strong> convention (e.g. 7.01, 15.01). If the row exists, Our% will be updated.
                    </p>
                  </div>
                  <div className="space-y-1">
                    <label className="text-xs font-medium text-gray-700">Our Margin %</label>
                    <Input
                      type="number" step="0.01" placeholder="e.g. 3.00"
                      value={newOur} onChange={(e) => setNewOur(e.target.value)}
                      data-testid="margin-master-new-our-input"
                    />
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setAddOpen(false)}>Cancel</Button>
                  <Button onClick={handleAddRow} className="bg-blue-600 hover:bg-blue-700" data-testid="margin-master-save-row-btn">
                    Save row
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-3 mb-3">
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                value={search} onChange={(e) => setSearch(e.target.value)}
                placeholder="Filter by Inward % or Our %"
                className="pl-8"
                data-testid="margin-master-search-input"
              />
            </div>
            <Badge variant="outline" className="text-xs">
              {loading ? '…' : filtered.length + ' / ' + rows.length + ' rows'}
            </Badge>
            <Button variant="ghost" size="sm" onClick={loadRows} disabled={loading} data-testid="margin-master-refresh-btn">
              <RefreshCw className={'w-4 h-4 ' + (loading ? 'animate-spin' : '')} />
            </Button>
          </div>

          <div className="border rounded-lg overflow-hidden">
            <table className="w-full text-sm" data-testid="margin-master-table">
              <thead className="bg-slate-50 border-b">
                <tr className="text-left text-xs uppercase text-gray-500">
                  <th className="px-3 py-2 w-32">Inward Margin %</th>
                  <th className="px-3 py-2 w-32">Our Margin %</th>
                  <th className="px-3 py-2"> </th>
                  <th className="px-3 py-2 w-24 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {loading && (
                  <tr><td colSpan={4} className="px-3 py-8 text-center text-gray-400">
                    <Loader2 className="w-5 h-5 animate-spin inline mr-2" /> Loading…
                  </td></tr>
                )}
                {!loading && filtered.length === 0 && (
                  <tr><td colSpan={4} className="px-3 py-8 text-center text-gray-400">
                    No rows match. Try clearing the filter or click <strong>Reseed</strong>.
                  </td></tr>
                )}
                {!loading && filtered.map((r) => {
                  const inward = Number(r.inward_margin_pct);
                  const our = Number(r.our_margin_pct);
                  const isEditing = editingKey !== null && Math.abs(editingKey - inward) < 0.005;
                  const isSaving = savingKey !== null && Math.abs(savingKey - inward) < 0.005;
                  return (
                    <tr key={inward.toFixed(2)} className="hover:bg-slate-50/60" data-testid={'margin-row-' + inward.toFixed(2)}>
                      <td className="px-3 py-2 font-mono">{inward.toFixed(2)}%</td>
                      <td className="px-3 py-2">
                        {isEditing ? (
                          <Input
                            type="number" step="0.01" autoFocus
                            value={editingValue}
                            onChange={(e) => setEditingValue(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') handleSaveRow(inward, parseFloat(editingValue));
                              if (e.key === 'Escape') { setEditingKey(null); setEditingValue(''); }
                            }}
                            className="h-7 text-sm w-24"
                            data-testid={'margin-edit-input-' + inward.toFixed(2)}
                          />
                        ) : (
                          <button
                            type="button"
                            onClick={() => { setEditingKey(inward); setEditingValue(our.toFixed(2)); }}
                            className="font-mono hover:bg-blue-50 px-1.5 py-0.5 rounded transition"
                            title="Click to edit"
                            data-testid={'margin-our-cell-' + inward.toFixed(2)}
                          >
                            {our.toFixed(2)}%
                          </button>
                        )}
                      </td>
                      <td className="px-3 py-2 text-xs text-gray-400">
                        {isEditing && (
                          <span className="text-blue-600">
                            Press <kbd className="bg-gray-100 px-1.5 py-0.5 rounded text-[10px]">Enter</kbd> to save · <kbd className="bg-gray-100 px-1.5 py-0.5 rounded text-[10px]">Esc</kbd> to cancel
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-right">
                        {isEditing ? (
                          <div className="flex justify-end gap-1">
                            <Button size="sm" variant="ghost" disabled={isSaving}
                              onClick={() => handleSaveRow(inward, parseFloat(editingValue))}
                              data-testid={'margin-save-btn-' + inward.toFixed(2)}>
                              {isSaving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Save'}
                            </Button>
                            <Button size="sm" variant="ghost"
                              onClick={() => { setEditingKey(null); setEditingValue(''); }}>
                              Cancel
                            </Button>
                          </div>
                        ) : (
                          <Button
                            size="sm" variant="ghost" className="h-7 px-2 text-red-600 hover:text-red-700 hover:bg-red-50"
                            onClick={() => handleDelete(inward)}
                            data-testid={'margin-delete-btn-' + inward.toFixed(2)}>
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
